import { useCallback, useEffect, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { useSearchParams } from 'react-router-dom'

import { PageLink, PageTitle } from '../../../../_metronic/layout/core'
import { getListTickets } from '../core/_requests'
import { PrepareTicketsToObject, TicketListObject } from '../core/_models'
import ActiveTicketsFilter from './ActiveTicketsFilter'
import ActiveTicketItem from './ActiveTicketItem'
import { ROLE, useAuth } from '../../auth'
import { IsGranted } from '../../auth/PrivateRoute'
import { ETicketStatus } from '../../../../types/enums'
import { Table } from '../../../../components/Table'
import { TableHead } from '../../../../components/Table/TableHead'
import { TableBody } from '../../../../components/Table/TableBody'
import { NewTicket } from '../../../../components/NewTicket'

import '../../../assets/css/pagination.css'
import { Merchant } from '../../../common/core/_models'
import { getListMerchantsForSelect } from '../../../common/core/_requests'
import { FilterManager, IBaseFilters } from '../../../common/filter/FilterManager'
import {
  ADAPTER_FILTER_LABEL,
  ADAPTER_ID_SEARCH_ERROR,
  adapterIdPayload,
  isAdapterScopeRequired,
  isAdapterSearchBlocked,
  usePaymentAdapterOptions,
} from '../../../common/filter/adapterIdSearch'
import { DEFAULT_ACTIVE_STATUS_GROUPS, ACTIVE_STATUS_GROUP_OPTIONS } from '../../../common/core/ticketStatusGroups'
import { CONCIERGE_ROLES } from '../core/_conciergeModels'

const accountBreadCrumbs: Array<PageLink> = [
  {
    title: 'Активные',
    path: '/payment-tickets/active',
    isSeparator: false,
    isActive: true,
    roles: [
      ROLE.ROLE_ADMIN,
      ROLE.ROLE_MANAGER,
      ROLE.ROLE_CASHIER,
      ROLE.ROLE_SENIOR_CASHIER,
      ROLE.ROLE_SENIOR_OPERATOR,
      ROLE.ROLE_OPERATOR,
    ],
  },
  {
    title: 'Завершённые',
    path: '/payment-tickets/finished',
    isSeparator: false,
    isActive: false,
    roles: [
      ROLE.ROLE_ADMIN,
      ROLE.ROLE_MANAGER,
      ROLE.ROLE_CASHIER,
      ROLE.ROLE_SENIOR_CASHIER,
      ROLE.ROLE_SENIOR_OPERATOR,
      ROLE.ROLE_OPERATOR,
    ],
  },
]

const defaultTicketList: TicketListObject = {}
const perPage = 20

// Статусы по умолчанию для активных пополнений (что показывается сейчас).
const defaultActiveStatuses = [
  ETicketStatus.WAITING_FOR_SMS,
  ETicketStatus.WAITING_FOR_MERCHANT,
  ETicketStatus.WAITING_FOR_ACTION,
  ETicketStatus.NOT_CONFIRMED,
  ETicketStatus.PAYMENT_PENDING,
  ETicketStatus.MANUAL_REQUISITE,
]

export const TICKET_TOKEN_PARAM = 'ticketToken'

/** The list's starting filters, searched by the token a link brought in `?ticketToken=`, if any. */
export const withLinkedToken = (defaults: IBaseFilters, params: URLSearchParams): IBaseFilters => {
  const token = params.get(TICKET_TOKEN_PARAM)?.trim() ?? ''
  return token ? { ...defaults, searchAdvanced: { query: token, field: 'ticketToken' } } : defaults
}

// Исходные (дефолтные) значения фильтров для внутренних ролей — внутренние статусы.
const defaultFilterValues: IBaseFilters = {
  perPage,
  status: [...defaultActiveStatuses],
  searchAdvanced: { query: '', field: 'ticketToken' },
}

// Для мерчантских ролей (оператор/старший оператор) — бизнес-статусы (группы).
const defaultMerchantFilterValues: IBaseFilters = {
  perPage,
  status: [...DEFAULT_ACTIVE_STATUS_GROUPS],
  searchAdvanced: { query: '', field: 'ticketToken' },
}

const ActiveTickets = () => {
  const [initialized, setInitialized] = useState(false)
  const { currentUser } = useAuth()
  // Оператор/старший оператор — мерчантские роли: фильтруют по бизнес-статусам, а не по внутренним.
  // Бэкенд разворачивает группы только для сущности Operator с ROLE_OPERATOR. ROLE_PLATFORM (мерчант)
  // по иерархии тоже даёт ROLE_OPERATOR, но группы для него не разворачиваются — исключаем его здесь,
  // иначе платформа слала бы statusGroup[], который бэкенд игнорирует, и список остался бы без фильтра.
  const isMerchantRole =
    IsGranted([ROLE.ROLE_OPERATOR, ROLE.ROLE_SENIOR_OPERATOR], currentUser) &&
    !IsGranted([ROLE.ROLE_PLATFORM], currentUser)
  // A link from outside (the operator's chat) opens the list already searched by `?ticketToken=`.
  const [searchParams] = useSearchParams()
  const activeDefaults = withLinkedToken(isMerchantRole ? defaultMerchantFilterValues : defaultFilterValues, searchParams)
  const canConcierge = IsGranted(CONCIERGE_ROLES, currentUser)
  const [filters, setFilters] = useState<IBaseFilters>(activeDefaults)
  const [tickets, setTickets] = useState(defaultTicketList)
  const [isLoading, setIsLoading] = useState(false)
  const adapterRequired = isAdapterScopeRequired(currentUser)
  const [searchError, setSearchError] = useState('')
  const adapterOptions = usePaymentAdapterOptions(adapterRequired)
  const [countPages, setCountPages] = useState(0)
  const [fullCount, setFullCount] = useState(0)
  const [showAlert, setShowAlert] = useState(false)
  const [merchantsList, setMerchantsList] = useState<Array<Merchant>>([])


  const getTicketList = useCallback(
    async (query: any) => {
      setIsLoading(true)
      try {
        const response = await getListTickets(
          query,
          currentUser
        )
        if (!response?.data) {
          throw new Error(
            'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
          )
        }
        setTickets({ ...PrepareTicketsToObject(response.data.tickets || {}) })
        setCountPages(Math.ceil(response.data.totalRows / perPage))
        setFullCount(response.data.totalRows || 0)
      } catch (error) {
        console.error('Ошибка при загрузке тикетов:', error)
        setTickets({})
        setCountPages(0)
        setFullCount(0)
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const applyFilters = useCallback(
    (newFilters: IBaseFilters, pagination?: { page: number, perPage: number }) => {
      if (isAdapterSearchBlocked(adapterRequired, newFilters.searchAdvanced, 'paymentId', newFilters.paymentAdapterId)) {
        setSearchError(ADAPTER_ID_SEARCH_ERROR)
        return
      }
      setSearchError('')
      // Сохраняем применённые фильтры как источник истины, чтобы пагинация/обновление списка
      // (onPageChange, NewTicket, updateTicketList) не сбрасывали выбор статусов на дефолт.
      setFilters(newFilters)

      const query: Record<string, any> = {
        page: pagination?.page || 1,
        perPage: pagination?.perPage || perPage,
        euConfirmed: newFilters.euConfirmed,
        // Мерчантские роли шлют бизнес-статусы (backend развернёт их во внутренние),
        // внутренние роли — как раньше, внутренние статусы.
        ...(isMerchantRole ? { statusGroup: newFilters.status } : { status: newFilters.status }),
        // Живой консьерж-тикет идёт через поиск, выдачу и чек — статусы вне фильтра; без флага он
        // пропадал бы из активных посреди работы с ним. Флаг знает только внутренний список.
        ...(canConcierge ? { withLiveConcierge: true } : {}),
      };

      if (newFilters.searchAdvanced?.query?.trim() && newFilters.searchAdvanced.field) {
        query[newFilters.searchAdvanced.field] = newFilters.searchAdvanced.query;
      }
      Object.assign(query, adapterIdPayload(adapterRequired, newFilters.searchAdvanced, 'paymentId', newFilters.paymentAdapterId));

      getTicketList(query)
    },
    [getTicketList, adapterRequired, isMerchantRole, canConcierge]
  )

  const removeTicketFromList = (token: string) => {
    let ticketsClone = Object.assign(tickets, {})

    delete ticketsClone[token]

    setTickets(ticketsClone)
    setFullCount(fullCount - 1)
  }

  useEffect(() => {
    if (!initialized) {
      applyFilters(filters, { page: 1, perPage })
      if (IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_CASHIER, ROLE.ROLE_CASHIER, ROLE.ROLE_SENIOR_OPERATOR, ROLE.ROLE_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser)) {
        getListMerchantsForSelect(currentUser).then((res) =>
          setMerchantsList(res?.data?.merchants || [])
        )
      }
      setInitialized(true)
    }
  }, [initialized, applyFilters, filters, perPage])


  return (
    <>
      {IsGranted(
        [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_OPERATOR, ROLE.ROLE_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER],
        currentUser
      ) && (
          <div className='row'>
            <div className='col text-end pb-4'>
              <NewTicket callback={() => applyFilters(filters)} />
            </div>
          </div>
        )}

      <div className='card'>
        <PageTitle breadcrumbs={accountBreadCrumbs}>Активные</PageTitle>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Активные тикеты</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>Количество: {fullCount}</span>
          </h3>
        </div>

        <FilterManager
          activeFilters={['searchAdvanced', { filter: 'select', fieldName: 'paymentAdapterId', visibility: adapterRequired }, {
            filter: 'selectMulti',
            fieldName: 'status',
            // Фильтр статуса — только оператору/старшему оператору (по тикету CPAYMENT-1589).
            visibility: isMerchantRole,
          }]}
          onApplyFilters={applyFilters}
          filterProps={{
            searchAdvanced: {
              options: [
                { label: 'ID тикета',     value: 'ticketToken' },
                { label: 'ID транзакции', value: 'transactionId' },
                { label: 'ID адаптера',   value: 'paymentId' },
              ],
              placeholder: 'Введите значение...',
              error: searchError,
            },
            paymentAdapterId: {
              label: ADAPTER_FILTER_LABEL,
              options: adapterOptions,
              placeholder: 'Не выбран',
            },
            status: {
              label: 'Статус',
              options: ACTIVE_STATUS_GROUP_OPTIONS,
              placeholder: 'Все статусы',
            },
          }}
          defaultValues={activeDefaults}
        />

        <div className='card-body py-3'>
          {showAlert && (
            <div
              style={{
                top: '10vh',
                left: '50%',
                zIndex: 10000,
                transform: 'translate(-50%, -50%)',
              }}
              className='alert alert-success position-fixed min-w-200px w-300px'
            >
              Скопировано
            </div>
          )}
          <Table>
            <TableHead>
              <tr>
                <th className='min-w-120px'>ID тикета</th>
                <th className='w-130px'>ID транзакции</th>
                <th className='min-w-120px'>Дата</th>
                {IsGranted(
                  [
                    ROLE.ROLE_ADMIN,
                    ROLE.ROLE_MANAGER,
                    ROLE.ROLE_CASHIER,
                    ROLE.ROLE_SENIOR_CASHIER,
                    ROLE.ROLE_SENIOR_OPERATOR,
                    ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER,
                  ],
                  currentUser
                ) && <th className='min-w-120px'>Сумма к переводу / факт</th>}
                <th className='min-w-120px'>ПС</th>
                <th className='min-w-140px'>Реквизит</th>
                {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                  <th className='min-w-100px'>Касса</th>
                )}
                <th className='min-w-100px'>Клиент</th>
                <th className='min-w-150px'>Смс</th>
                {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                  <th className='min-w-120px'>Мерчант</th>
                )}
                {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_OPERATOR, ROLE.ROLE_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                  <th className='min-w-80px'>Статус плат.адаптера</th>
                )}
                <th className='min-w-100px'>Отметить как фейковый</th>
                {canConcierge && <th className='min-w-100px'>Консьерж</th>}
                <th className='min-w-150px'></th>
                <th className='min-w-50px'></th>
              </tr>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <tr>
                  <td colSpan={10}>Загрузка...</td>
                </tr>
              ) : (
                <>
                  {Object.keys(tickets).map((key) => (
                    <ActiveTicketItem
                      ticket={tickets[key]}
                      merchants={merchantsList}
                      removeTicketFromList={removeTicketFromList}
                      updateTicketList={() => applyFilters(filters)}
                      setShowAlert={setShowAlert}
                      setTickets={setTickets}
                      key={key}
                    />
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </div>

        <ReactPaginate
          activeClassName={'active-pagination '}
          breakClassName={'item-pagination break-me-pagination '}
          containerClassName={'pagination-pagination'}
          disabledClassName={'disabled-page-pagination'}
          nextClassName={'item-pagination ki-duotone ki-right fs-2x'}
          pageClassName={'item-pagination pagination-page-pagination '}
          previousClassName={'item-pagination ki-duotone ki-left fs-2x'}
          breakLabel='...'
          nextLabel=''
          onPageChange={(selected) =>
            applyFilters({ ...filters }, { page: selected.selected + 1, perPage })
          }
          pageRangeDisplayed={5}
          pageCount={countPages}
          previousLabel=''
          renderOnZeroPageCount={null}
        />
      </div>
    </>
  )
}

export default ActiveTickets
